import { ROUTES } from "../../lib/config";
import { ORGANISATION_TYPE } from "../../lib/organisations";
import LayoutDashboard from "lucide-solid/icons/layout-dashboard";
import LayoutGrid from "lucide-solid/icons/layout-grid";
import MessageSquare from "lucide-solid/icons/message-square";
import MapPin from "lucide-solid/icons/map-pin";
import Database from "lucide-solid/icons/database";
import FileText from "lucide-solid/icons/file-text";
import MessageCircleQuestionMark from "lucide-solid/icons/message-circle-question-mark";
import type { Component, ComponentProps } from "solid-js";

export type MobilePosition = "bar" | "more";

export type DesktopSection = "primary" | "utility";

export type NavigationItem = {
  label: string;
  href: string;
  icon?: Component<ComponentProps<"svg">>;
  children?: NavigationItem[];
  mobilePosition: MobilePosition;
  mobileLabel?: string;
  desktopGroup?: number;
  desktopSection?: DesktopSection;
};

export type NavigationConfig = {
  organisationType?: string | null;
  overviewPageEnabled?: boolean;
  overviewLabel?: string | null;
};

export function buildNavigationItems(config?: NavigationConfig): NavigationItem[] {
  const askLabel = config?.organisationType === ORGANISATION_TYPE.LOCAL_GOVERNMENT ? "Ask My Community" : "Ask";

  const items: NavigationItem[] = [
    { label: "Dashboard", href: ROUTES.dashboard, icon: LayoutDashboard, mobilePosition: "bar", desktopGroup: 1 },
    { label: askLabel, href: ROUTES.chat, icon: MessageCircleQuestionMark, mobilePosition: "bar", desktopGroup: 3 },
    { label: "Themes & Topics", href: ROUTES.topics, icon: MessageSquare, mobilePosition: "bar", mobileLabel: "Themes", desktopGroup: 1 },
    { label: "Map", href: ROUTES.map, icon: MapPin, mobilePosition: "bar", desktopGroup: 2 },
    { label: "Data Bank", href: ROUTES.dataBank, icon: Database, mobilePosition: "more", desktopSection: "utility" },
    { label: "Evidence Builder", href: ROUTES.reports, icon: FileText, mobilePosition: "more", desktopGroup: 3 },
  ];

  const overviewLabel = config?.overviewLabel?.trim();
  if (config?.overviewPageEnabled && overviewLabel) {
    const themesIndex = items.findIndex((item) => item.label === "Themes & Topics");
    items.splice(themesIndex + 1, 0, {
      label: overviewLabel,
      href: ROUTES.overview,
      icon: LayoutGrid,
      mobilePosition: "more",
      desktopGroup: 2,
    });
  }

  return items;
}

// Desktop sidebar order is driven by desktopGroup / desktopSection rather than
// array position. The mobile bar and More panel still derive their order from
// array position via mobilePosition.
export function getDesktopNavigationGroups(config?: NavigationConfig): NavigationItem[][] {
  const groups = new Map<number, NavigationItem[]>();
  for (const item of buildNavigationItems(config)) {
    if (item.desktopSection === "utility") {
      continue;
    }
    if (item.desktopGroup === undefined) {
      throw new Error(
        `Primary navigation item "${item.label}" must declare a desktopGroup`,
      );
    }
    const existing = groups.get(item.desktopGroup);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(item.desktopGroup, [item]);
    }
  }
  return [...groups.keys()].sort((a, b) => a - b).map((key) => groups.get(key)!);
}

export function getDesktopUtilityItems(config?: NavigationConfig): NavigationItem[] {
  return buildNavigationItems(config).filter(
    (item) => item.desktopSection === "utility",
  );
}

export function getMobileBarItems(config?: NavigationConfig): NavigationItem[] {
  return buildNavigationItems(config).filter((item) => item.mobilePosition === "bar");
}

export function getMobileMoreItems(config?: NavigationConfig): NavigationItem[] {
  return buildNavigationItems(config).filter((item) => item.mobilePosition === "more");
}
